/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APIUserResponseInterface,
  RequestUserInterFace,
  UserActivityLogs,
  UserInitialProperties,
  UserResponseInterFace,
  SessionUserProperties,
} from '../../interface/users-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { arrayGroupBy, removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import userApiPartner from '../../utils/api/partner/users';
import userApi from '../../utils/api/users';

import { setNotificationMessage } from './NotificationManagementSlice';

// a thunk to fetch users
export const fetchAllUsers = createAsyncThunk<APIUserResponseInterface, RequestUserInterFace>(
  'user-management/fetchAllUsers',
  async (data: RequestUserInterFace, { dispatch }) => {
    const response = await userApi
      .fetchUsers(removeEmptyKeys(data))
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((roleData: UserResponseInterFace) => ({
        ...roleData,
        roleName: roleData?.role?.name,
        role: roleData?.role?._id,
        id: roleData._id,
      })),
    };
  },
);
// Refresh users without toggling loading state
export const refreshUsers = createAsyncThunk<APIUserResponseInterface, RequestUserInterFace>(
  'user-management/refreshUsers',
  async (data: RequestUserInterFace, { dispatch }) => {
    try {
      const response = await userApi.fetchUsers(removeEmptyKeys(data));
      return {
        ...response.data,
        results: response.data.results.map((roleData: UserResponseInterFace) => ({
          ...roleData,
          roleName: roleData?.role?.name,
          role: roleData?.role?._id,
          id: roleData._id,
        })),
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);
// Fetch session user
export const fetchUserActivityLogs = createAsyncThunk<UserActivityLogs[], String>(
  'user-management/fetchUserActivityLogs',
  async (id: String, { dispatch }) => {
    try {
      const response = await userApi.fetchUserActivityLogs(id);
      return response.data.map((userData: any) => ({
        ...userData,
        date: ConvertUTCtoDate(userData.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(userData.createdAt)),
        id: userData._id,
      }));
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchUsersFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('user-management/fetchUsersFilters', async (_, { dispatch }) => {
  try {
    const response = await userApi.fetchUsersListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(
      response.data,
    ).filter((filter) => filter.type !== filterType.DATE_RANGE);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});
// Fetch session user
export const fetchSessionUser = createAsyncThunk<SessionUserProperties, string>(
  'user-management/fetchSessionUser',
  async (id: string) => {
    const response = await userApiPartner.fetchUser(id);
    return response.data;
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: UserInitialProperties = {
  users: [],
  userActiveLogs: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  loggingLoading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
  sessionUser: {},
};

// Create the slice
export const userSlice = createSlice({
  name: 'users-management',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state The current slice state.
     * @param {any} action The dispatched action containing filter update properties.
     */
    updateUsersFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state The current slice state.
     */
    disableUsersFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The dispatched action containing sort field and order.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The builder for handling extra reducers.
   */
  extraReducers: (builder: ActionReducerMapBuilder<UserInitialProperties>) => {
    builder
      .addCase(fetchAllUsers.pending, (state: UserInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllUsers.fulfilled,
        (state: UserInitialProperties, action: PayloadAction<APIUserResponseInterface>) => {
          state.loading = false;
          state.users = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllUsers.rejected, (state: UserInitialProperties) => {
        state.loading = false;
      })
      .addCase(
        refreshUsers.fulfilled,
        (state: UserInitialProperties, action: PayloadAction<APIUserResponseInterface>) => {
          state.users = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      );
    builder
      .addCase(fetchUserActivityLogs.pending, (state) => {
        state.loggingLoading = true;
      })
      .addCase(
        fetchUserActivityLogs.fulfilled,
        (state, action: PayloadAction<UserActivityLogs[]>) => {
          state.loggingLoading = false;
          state.userActiveLogs = Object.entries(arrayGroupBy(action.payload, 'date')).map(
            (value) => ({
              logs: value[1].reverse(),
              date: value[0],
            }),
          );
        },
      )
      .addCase(fetchUserActivityLogs.rejected, (state) => {
        state.loggingLoading = false;
      });
    builder
      .addCase(fetchUsersFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchUsersFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...availableFilters, ...action.payload].filter(
            (object, index, self) =>
              index === self.findIndex((t) => t.key === object.key) &&
              object.type !== filterType.DATE_RANGE,
          );
        },
      )
      .addCase(fetchUsersFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
    builder.addCase(
      fetchSessionUser.fulfilled,
      (state, action: PayloadAction<SessionUserProperties>) => {
        state.sessionUser = { ...action.payload, id: action.payload._id };
      },
    );
  },
});

// Export the actions
export const { updateUsersFilters, disableUsersFilterApplied, setSortData } = userSlice.actions;
// Export the reducer
export default userSlice.reducer;
